<script setup lang="ts">
import {months} from "@/helpers/constants.ts";
import {computed, ref, watch} from "vue";

const model = defineModel<string>()
const props = defineProps<{
  minYear: number,
  showErrors: boolean,
}>()

const [y, m, d] = (model.value || '').split('-')

const day = ref(d ? Number(d) : 0)
const month = ref(m ? Number(m) - 1 : -1)
const year = ref(y ? Number(y) : 0)

const dobAsDate = computed(() => {
  return new Date(
    Date.UTC(
      year.value,
      month.value,
      day.value,
      0,0,0
    )
  )
})

const dobIsValid = computed(() => {
  const dob = dobAsDate.value;

  return dob.getUTCFullYear() === year.value &&
    dob.getUTCMonth() === month.value &&
    dob.getUTCDate() === day.value
})

const dobAsDateOnly = computed(() => {
  if (dobIsValid.value) {
    return dobAsDate.value.toISOString().split('T').shift()
  }

  return ''
})

const daysOptions = computed(() =>
  Array.from({length: 31}).map((_, index) => ({value: index + 1, label: index + 1}))
)

const monthOptions = computed(() =>
  months.map((m, index) => ({value: index, label: m}))
)

const yearOptions = computed(() =>
  Array.from({length: 100}).map((_, index) => ({
    value: props.minYear - index,
    label: props.minYear - index
  }))
)

watch(() => dobAsDateOnly.value, () => {
  if (dobIsValid.value) {
    model.value = dobAsDateOnly.value
  }
})
</script>

<template>
  <div class="flex space-x-4 mb-8">
    <div class="w-1/3">
      <label class="block text-sm text-gray-500 mb-1">Day</label>
      <select class="w-full py-2 px-3 border border-gray-300 rounded-lg focus:outline-none"
              :class="{ 'border-red-500': props.showErrors && !dobIsValid }" v-model="day">

        <option :value="0" disabled selected>Select</option>
        <option v-for="d in daysOptions" :key="`day-${d.value}`" :value="d.value">{{
            d.label
          }}
        </option>
      </select>
    </div>
    <div class="w-1/3">
      <label class="block text-sm text-gray-500 mb-1">Month</label>
      <select class="w-full py-2 px-3 border border-gray-300 rounded-lg focus:outline-none"
              v-model="month"
              :class="{ 'border-red-500': props.showErrors && !dobIsValid }">

        <option :value="-1" disabled selected>Select</option>
        <option v-for="m in monthOptions" :key="`month-${m.value}`" :value="m.value">{{
            m.label
          }}
        </option>
      </select>
    </div>
    <div class="w-1/3">
      <label class="block text-sm text-gray-500 mb-1">Year</label>
      <select class="w-full py-2 px-3 border border-gray-300 rounded-lg focus:outline-none"
              v-model="year"
              :class="{ 'border-red-500': props.showErrors && !dobIsValid }">

        <option :value="0" disabled selected>Select</option>
        <option v-for="y in yearOptions" :key="`year-${y.value}`" :value="y.value">{{
            y.label
          }}
        </option>
      </select>
    </div>
  </div>
</template>
